import React, {
  memo,
  useRef,
  useLayoutEffect,
  useState,
  useEffect,
} from 'react';
import './LabelEventGraphDemo.css';

// ── Types de labels ───────────────────────────────────────────────────────────

export const LABEL_TYPES = {
  IA: { iconType: 'circle', label: 'IA', color: '#0077b6' },
  JALON: { iconType: 'diamond', label: 'Jalon', color: '#e07b00' },
};

// ── Couleurs de phases ────────────────────────────────────────────────────────

export const COULEURS_PHASES = [
  '#e07b00',
  '#0077b6',
  '#26b36a',
  '#9b2226',
  '#6a0572',
  '#2f46d8',
  '#2d6a4f',
];

// ── Icône SVG (cercle ou diamant) ─────────────────────────────────────────────

export const GeoIcon = memo(({ iconType, size = 14 }) => {
  const entry = Object.values(LABEL_TYPES).find((t) => t.iconType === iconType);
  const color = entry ? entry.color : '#666';
  const r = size / 2;
  const svgProps = {
    width: size,
    height: size,
    viewBox: `0 0 ${size} ${size}`,
    style: { flexShrink: 0 },
  };
  if (iconType === 'diamond') {
    return (
      <svg {...svgProps}>
        <polygon
          points={`${r},1 ${size - 1},${r} ${r},${size - 1} 1,${r}`}
          fill={color}
        />
      </svg>
    );
  }
  return (
    <svg {...svgProps}>
      <circle cx={r} cy={r} r={r - 1} fill={color} />
    </svg>
  );
});

// ── Fonctions utilitaires ─────────────────────────────────────────────────────

const tsToDateStr = (ts) =>
  new Date(ts * 1000).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const tsToDateInput = (ts) => new Date(ts * 1000).toISOString().slice(0, 10);
const dateInputToTs = (val) => Math.floor(new Date(val).getTime() / 1000);

const tsToLabel = (ts, y) => {
  const n = Number(ts);
  if (!Number.isFinite(n)) return 'Date invalide';

  const date = new Date(n * 1000).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return y != null ? `${date} — ${Math.round(y * 10) / 10} dB` : date;
};

const parseSensorPositionFormula = (formula) => {
  const [sensorId, position] = formula
    .split(';')[0] // prend le premier bloc (ou le seul)
    .match(/\d+/g)
    .map(Number);
  return { sensorId, position };
};

const sensorPositionFormulaToLabel = (formula) => {
  const { sensorId, position } = parseSensorPositionFormula(formula);
  return `Capteur ${sensorId} - Position ${position}`;
};

const findDataBySensorAndPosition = (data, sensorId, position) => {
  if (!sensorId || position === undefined) return null;

  const position_in_sensor = Number(position);

  return data.find(
    (d) =>
      d.id_sensor === sensorId && d.position_in_sensor === position_in_sensor
  );
};

// ── Icônes internes ───────────────────────────────────────────────────────────

const PencilIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
  </svg>
);

const TrashIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
    <path
      fillRule="evenodd"
      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
    />
  </svg>
);

// ── Hook de positionnement intelligent (évite la sortie du viewport) ───────────

const useSmartPosition = (x, y) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({
    left: x + 14,
    top: y - 24,
    visibility: 'hidden',
  });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const MARGE = 8;
    let left = x + 14;
    let top = y - 24;
    // déborde à droite → afficher à gauche du curseur
    if (left + rect.width > window.innerWidth - MARGE) {
      left = x - rect.width - 8;
    }
    if (left < MARGE) left = MARGE;
    // déborde en bas
    if (top + rect.height > window.innerHeight - MARGE) {
      top = window.innerHeight - rect.height - MARGE;
    }
    // déborde en haut
    if (top < MARGE) top = MARGE;
    setPos({ left, top, visibility: 'visible' });
  }, [x, y]);

  return { ref, pos };
};

// ── Carte de survol — étiquette ──────────────────────────────────────────────
//
// ORIGINE DES DONNÉES (labelData)
// ─────────────────────────────────────────────────────────────────────────────
// 1. Au chargement du graphe, LabelEventGraphGeoDemo appelle fetchGraphData()
//    (labelEventGraphApi.js). La réponse contient un tableau `labels` (format
//    API snake_case) converti en camelCase par mapApiLabel() puis stocké dans
//    l'état `labels` du conteneur.
//
// 2. LabelEventGraph reçoit ce tableau via la prop configuration.labels.
//    Il génère des marqueurs Plotly invisibles (labelHoverTraces) positionnés
//    sur chaque label ; chaque marqueur embarque le timestamp `t` dans son
//    champ `customdata`.
//
// 3. Quand l'utilisateur survole un de ces marqueurs, le callback handleHover
//    de LabelEventGraph détecte que customdata est un timestamp (≠ object
//    phase) et appelle onLabelHover(pt.customdata, clientX, clientY).
//
// 4. Dans le conteneur, handleLabelHover recherche dans `labels` l'objet dont
//    `l.t === t`, puis appelle setHoveredLabel({ label: found, x, y }).
//
// 5. Le conteneur affiche <LabelHoverCard labelData={hoveredLabel.label} …/>
//    → c'est l'objet { id, t, labelType, iconType, label, note } issu de
//    l'état local, lui-même synchronisé avec l'API.
//
// COMMENT AJOUTER UN LABEL DEPUIS LE SURVOL DE LA COURBE
// ─────────────────────────────────────────────────────────────────────────────
// 1. En survolant la courbe principale (curveNumber === 0), handleHover dans
//    LabelEventGraph enregistre la position dans l'état `signalHover`
//    ({ ts, yVal, clientX, clientY }).
//
// 2. Un bouton flottant « + Ajouter un label » apparaît à la position de la
//    souris. Un clic déclenche onAddLabel({ ts, yVal }).
//
// 3. Dans le conteneur, handleAddLabelFromSignal crée un brouillon
//    { id: null, t, labelType: 'JALON', label: '', note: `Valeur : ${yVal} dB` }
//    et le place dans l'état `creatingLabel`.
//
// 4. <LabelEditDialog> s'affiche. À la sauvegarde, handleSaveNewLabel appelle
//    createLabel(graph_id, payload) (API) ; en cas de succès, le nouveau label
//    mappé est ajouté au tableau `labels` via setLabels(prev => [...prev, …]).
//    La carte de survol apparaîtra automatiquement lors du prochain survol.

export const LabelHoverCard = memo(
  ({ labelData, x, y, onEdit, onRemove, onMouseEnter, onMouseLeave }) => {
    const labelConfig = LABEL_TYPES[labelData.labelType] || LABEL_TYPES.IA;
    const { ref, pos } = useSmartPosition(x, y);

    useEffect(() => {
      console.log('[LabelHoverCard] survol — données label :', labelData);
    }, [labelData]);
    return (
      <div
        ref={ref}
        className="leg-hover-anchor"
        style={{ left: pos.left, top: pos.top, visibility: pos.visibility }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          className="leg-hover-card"
          style={{ borderColor: labelConfig.color }}
        >
          <div className="leg-card-header">
            <div className="leg-card-type">
              <GeoIcon iconType={labelData.iconType} size={13} />
              <span
                className="leg-type-badge"
                style={{ color: labelConfig.color }}
              >
                {labelConfig.label}
              </span>
            </div>
            <div className="leg-card-btn-group">
              <button className="leg-btn-edit" onClick={onEdit} title="Éditer">
                <PencilIcon size={14} />
              </button>
              <button
                className="leg-btn-trash"
                onClick={onRemove}
                title="Supprimer"
              >
                <TrashIcon size={14} />
              </button>
            </div>
          </div>
          <div
            className="leg-divider"
            style={{ borderColor: labelConfig.color + '44' }}
          />
          <div className="leg-card-row">
            <span className="leg-card-meta">Date</span>
            <span className="leg-card-value">{tsToDateStr(labelData.t)}</span>
          </div>
          {labelData.label && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Label</span>
              <span className="leg-card-value leg-card-value--strong">
                {labelData.label}
              </span>
            </div>
          )}
          {labelData.note && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Note</span>
              <span className="leg-card-value leg-card-value--italic">
                {labelData.note}
              </span>
            </div>
          )}
          {labelData.labelType === 'IA' && labelData.precision != null && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Précision</span>
              <span className="leg-card-value">{labelData.precision} %</span>
            </div>
          )}
          {labelData.labelType === 'IA' && labelData.urlSon && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Son</span>
              <a
                href={labelData.urlSon}
                target="_blank"
                rel="noopener noreferrer"
                className="leg-sound-link"
                onClick={(e) => e.stopPropagation()}
              >
                {labelData.urlSon}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// ── Carte de survol — phase ──────────────────────────────────────────────────
//
// ORIGINE DES DONNÉES (phaseData)
// ─────────────────────────────────────────────────────────────────────────────
// 1. Au chargement, fetchGraphData() retourne un tableau `sections` (API)
//    converti par mapApiSection() en { id, tStart, tEnd, label, color } et
//    stocké dans l'état `phases` du conteneur.
//
// 2. LabelEventGraph reçoit les phases via configuration.sections.
//    Il crée deux types de déclencheurs Plotly :
//      • phaseHoverTraces  : 20 points invisibles couvrant toute la largeur de
//        la phase, avec customdata = { type:'phase', tStart, tEnd, label, color }.
//      • annotations cliquables (captureevents:true) affichant le nom de phase.
//
// 3a. Clic sur un marqueur invisible → handleClick détecte
//     customdata.type === 'phase' et appelle onPhaseClick(pt.customdata, x, y).
//
// 3b. Clic sur l'annotation → handleClickAnnotation reconstruit l'objet phase
//     depuis sectionsRef.current[e.index] et appelle onPhaseClick de même.
//
// 4. Dans le conteneur, handlePhaseClick recherche dans `phases` la phase
//    correspondante (par tStart), puis appelle setEditingPhase({ …found }).
//    C'est donc <PhaseEditDialog> qui s'ouvre (et non cette carte de survol) ;
//    cette carte n'est affichée que si le conteneur route explicitement vers
//    setHoveredPhase — à implémenter si un mode survol pur est souhaité.

export const PhaseHoverCard = memo(
  ({ phaseData, x, y, onEdit, onRemove, onMouseEnter, onMouseLeave }) => {
    const color = phaseData.color || '#888888';
    const { ref, pos } = useSmartPosition(x, y);

    useEffect(() => {
      console.log('[PhaseHoverCard] survol — données phase :', phaseData);
    }, [phaseData]);
    return (
      <div
        ref={ref}
        className="leg-hover-anchor"
        style={{ left: pos.left, top: pos.top, visibility: pos.visibility }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="leg-hover-card" style={{ borderColor: color }}>
          <div className="leg-card-header">
            <div className="leg-card-type">
              <svg
                width="14"
                height="8"
                viewBox="0 0 14 8"
                style={{ flexShrink: 0 }}
              >
                <rect x="0" y="2" width="14" height="4" rx="2" fill={color} />
              </svg>
              <span className="leg-type-badge" style={{ color }}>
                Phase
              </span>
            </div>
            <div className="leg-card-btn-group">
              <button className="leg-btn-edit" onClick={onEdit} title="Éditer">
                <PencilIcon size={14} />
              </button>
              <button
                className="leg-btn-trash"
                onClick={onRemove}
                title="Supprimer"
              >
                <TrashIcon size={14} />
              </button>
            </div>
          </div>
          <div className="leg-divider" style={{ borderColor: color + '44' }} />
          <div className="leg-card-row">
            <span className="leg-card-meta">Début</span>
            <span className="leg-card-value">
              {tsToDateStr(phaseData.tStart)}
            </span>
          </div>
          <div className="leg-card-row">
            <span className="leg-card-meta">Fin</span>
            <span className="leg-card-value">
              {tsToDateStr(phaseData.tEnd)}
            </span>
          </div>
          {phaseData.label && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Nom</span>
              <span className="leg-card-value leg-card-value--strong">
                {phaseData.label}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// ── Dialogue d'édition — phase ───────────────────────────────────────────────

export const PhaseEditDialog = memo(({ phase, onSave, onCancel, onDelete }) => {
  const [draft, setDraft] = useState({ ...phase });
  const color = draft.color || '#888888';

  const setField = (field, value) =>
    setDraft((d) => ({
      ...d,
      [field]:
        field === 'tStart' || field === 'tEnd' ? dateInputToTs(value) : value,
    }));

  return (
    <div className="leg-overlay" onMouseDown={onCancel}>
      <div className="leg-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="leg-dialog-header">
          <div className="leg-dialog-title-row">
            <svg width="16" height="10" viewBox="0 0 16 10">
              <rect x="0" y="3" width="16" height="4" rx="2" fill={color} />
            </svg>
            <span className="leg-dialog-title">Éditer la phase</span>
          </div>
          <div className="leg-dialog-header-actions">
            {onDelete && (
              <button
                className="leg-btn-trash"
                onClick={() => onDelete(draft)}
                title="Supprimer"
              >
                <TrashIcon size={14} />
              </button>
            )}
            <button className="leg-dialog-close" onClick={onCancel}>
              ✕
            </button>
          </div>
        </div>
        {/* TODO ajouter un sélecteur pour choisir si on crée la phase sur un capteur + position ou sur le graphe directement*/}
        <div className="leg-form-grid">
          <span className="leg-field-label">Nom</span>
          <input
            type="text"
            value={draft.label || ''}
            placeholder="Nom de la phase…"
            onChange={(e) => setField('label', e.target.value)}
            className="leg-input"
          />
          <span className="leg-field-label">Début</span>
          <input
            type="date"
            value={tsToDateInput(draft.tStart)}
            onChange={(e) => setField('tStart', e.target.value)}
            className="leg-input"
          />
          <span className="leg-field-label">Fin</span>
          <input
            type="date"
            value={tsToDateInput(draft.tEnd)}
            onChange={(e) => setField('tEnd', e.target.value)}
            className="leg-input"
          />
          <span className="leg-field-label">Couleur</span>
          <input
            type="color"
            value={draft.color}
            onChange={(e) => setField('color', e.target.value)}
            className="leg-color-picker"
          />
        </div>
        <div className="leg-dialog-actions">
          <button className="leg-btn-clear" onClick={onCancel}>
            Annuler
          </button>
          <button className="leg-btn-add" onClick={() => onSave(draft)}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
});

// ── Dialogue d'édition — étiquette ───────────────────────────────────────────

export const LabelEditDialog = memo(
  ({ label, data, sensorsPositions, onSave, onCancel }) => {
    const [dateOptions, setDateOptions] = useState([]);
    const [draft, setDraft] = useState({ ...label });

    const setField = (field, value) => {
      if (field === 'sensorAndPosition') {
        const { sensorId, position } = parseSensorPositionFormula(value);
        const selectedData = findDataBySensorAndPosition(
          data,
          sensorId,
          position
        );

        if (selectedData) {
          const options = selectedData.x.map((ts, i) => ({
            ts,
            y: selectedData.y[i],
          }));
          setDateOptions(options);
        }
        setDraft((d) => ({
          ...d,
          sensorAndPosition: value,
        }));
        return;
      }

      if (field === 'labelType') {
        setDraft((d) => ({
          ...d,
          labelType: value,
          iconType: LABEL_TYPES[value].iconType,
        }));
      } else {
        setDraft((d) => ({
          ...d,
          [field]: field === 't' ? Number(value) : value,
        }));
      }
    };

    const labelConfig = LABEL_TYPES[draft.labelType] || LABEL_TYPES.IA;
    return (
      <div className="leg-overlay" onMouseDown={onCancel}>
        <div className="leg-dialog" onMouseDown={(e) => e.stopPropagation()}>
          <div className="leg-dialog-header">
            <div className="leg-dialog-title-row">
              <GeoIcon iconType={draft.iconType} size={15} />
              <span className="leg-dialog-title">Éditer le label</span>
            </div>
            <button className="leg-dialog-close" onClick={onCancel}>
              ✕
            </button>
          </div>
          <div className="leg-form-grid">
            <span className="leg-field-label">Courbe</span>
            <select
              value={draft.sensorAndPosition}
              onChange={(e) => setField('sensorAndPosition', e.target.value)}
              size={4}
              className="leg-select-list"
            >
              {sensorsPositions.map(({ formula }) => {
                return (
                  <option key={formula} value={formula}>
                    {sensorPositionFormulaToLabel(formula)}
                  </option>
                );
              })}
            </select>

            {dateOptions.length > 0 && (
              <>
                <span className="leg-field-label">Date</span>
                <select
                  value={draft.t}
                  onChange={(e) => setField('t', Number(e.target.value))}
                  size={4}
                  className="leg-select-list"
                >
                  {dateOptions.map(({ ts, y }) => (
                    <option key={ts} value={ts}>
                      {tsToLabel(ts, y)}
                    </option>
                  ))}
                </select>
              </>
            )}
            <span className="leg-field-label">Label</span>
            <input
              type="text"
              value={draft.label || ''}
              placeholder="label…"
              onChange={(e) => setField('label', e.target.value)}
              className="leg-input"
            />
          </div>
          <div className="leg-dialog-actions">
            <button className="leg-btn-clear" onClick={onCancel}>
              Annuler
            </button>
            <button className="leg-btn-add" onClick={() => onSave(draft)}>
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    );
  }
);
